import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  FC,
  PropsWithChildren,
  Dispatch,
  SetStateAction
} from "react";
import { firstValueFrom } from "rxjs";
import { Recipe, Segment, DataManagementPlatform } from "@madhive/mad-sdk";
import { madSDK } from "lib/sdk";
import {
  RECIPE_LOADING_STRATEGY,
  type RecipeLoadingStrategy
} from "./constants";
import {
  MINIMAL_RECIPE_FILTERS,
  DEFAULT_RECIPE_FILTERS
} from "./recipeFilters";
import {
  DEFAULT_SEGMENT_FILTERS,
  ARCHIVED_SEGMENT_FILTERS
} from "./segmentFilters";
import { convertArrayToKeyedObject, canCacheSatisfyStrategy } from "./utils";
import { subscribeToSegmentInvalidation } from "./segmentInvalidation";

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface AudienceDataContextValue {
  // Recipe state
  recipes: Record<string, Recipe> | undefined;
  setRecipes: Dispatch<SetStateAction<Record<string, Recipe> | undefined>>;
  recipesStrategy: RecipeLoadingStrategy | undefined;
  recipesLoading: boolean;
  setRecipesLoading: Dispatch<SetStateAction<boolean>>;

  // Segment state
  segments: Record<string, Segment> | undefined;
  setSegments: Dispatch<SetStateAction<Record<string, Segment> | undefined>>;
  segmentsLoading: boolean;
  setSegmentsLoading: Dispatch<SetStateAction<boolean>>;
  segmentsError: string | null;

  // DMP state
  dmps: DataManagementPlatform[] | undefined;
  setDmps: Dispatch<SetStateAction<DataManagementPlatform[] | undefined>>;
  dmpsLoading: boolean;
  setDmpsLoading: Dispatch<SetStateAction<boolean>>;

  // Archived segments background loading state
  archivedSegmentsLoaded: boolean;
  archivedSegmentsError: string | null;

  // Methods
  loadRecipes: (strategy: RecipeLoadingStrategy) => Promise<void>;
  loadSegments: () => Promise<void>;
  loadArchivedSegments: () => Promise<void>;
  loadDmps: () => Promise<void>;

  // Ref to track pending field updates (preserve during merges)
  pendingUpdatesRef: React.MutableRefObject<Map<string, Partial<Recipe>>>;

  // Ref to track in-progress loads
  inProgressLoadsRef: React.MutableRefObject<Set<Promise<void>>>;

  // Ref to track latest recipes synchronously (to avoid stale state reads)
  recipesRef: React.MutableRefObject<Record<string, Recipe> | undefined>;

  // Ref to track latest segments synchronously (to avoid stale state reads)
  segmentsRef: React.MutableRefObject<Record<string, Segment> | undefined>;
}

// ============================================================================
// CONTEXT
// ============================================================================

const AudienceDataContext = createContext<AudienceDataContextValue | undefined>(
  undefined
);

// ============================================================================
// CONTEXT HOOK
// ============================================================================

export const useAudienceDataContext = () => {
  const contextValue = useContext(AudienceDataContext);
  if (!contextValue) {
    throw new Error(
      "useAudienceDataContext must be used within AudienceDataProvider"
    );
  }
  return contextValue;
};

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export const AudienceDataProvider: FC<PropsWithChildren> = ({ children }) => {
  // Recipe state - combined to ensure atomic updates
  const [recipeState, setRecipeState] = useState<{
    data: Record<string, Recipe> | undefined;
    strategy: RecipeLoadingStrategy | undefined;
    loading: boolean;
  }>({
    data: undefined,
    strategy: undefined,
    loading: true
  });
  const recipesStrategyRef = useRef<RecipeLoadingStrategy | undefined>(
    undefined
  );

  // Ref to track latest recipes synchronously (must be defined BEFORE setRecipes uses it)
  const recipesRef = useRef<Record<string, Recipe> | undefined>(undefined);

  // Derived values for backward compatibility
  const recipes = recipeState.data;
  const recipesStrategy = recipeState.strategy;
  const recipesLoading = recipeState.loading;

  const setRecipes = useCallback(
    (updater: SetStateAction<Record<string, Recipe> | undefined>) => {
      setRecipeState(prev => {
        const newData =
          typeof updater === "function" ? updater(prev.data) : updater;
        // Update ref synchronously so subsequent operations have latest data
        recipesRef.current = newData;
        return {
          ...prev,
          data: newData
        };
      });
    },
    []
  );

  const setRecipesLoading = useCallback((loading: boolean) => {
    setRecipeState(prev => ({
      ...prev,
      loading
    }));
  }, []);

  // Segment state
  const [segments, setSegmentsState] = useState<
    Record<string, Segment> | undefined
  >(undefined);
  const [segmentsLoading, setSegmentsLoading] = useState(true);
  const [segmentsError, setSegmentsError] = useState<string | null>(null);

  // Ref to track latest segments synchronously (to avoid stale state reads)
  const segmentsRef = useRef<Record<string, Segment> | undefined>(undefined);

  // Ref to prevent concurrent segment load cycles (re-renders during retries
  // would otherwise restart the retry loop via the useEffect in useAudienceData)
  const isLoadingSegmentsRef = useRef(false);

  // Ref to prevent retrying after all attempts are exhausted — without this,
  // unrelated re-renders (e.g. dmps loading) recreate loadDmps, re-fire
  // useEffect([loadSegments, loadDmps]), and reset segmentsError to null
  const segmentLoadFailedRef = useRef(false);

  // Bumped on invalidation so a load already in flight can tell its snapshot
  // predates the change and refetch instead of caching stale segments
  const segmentsGenerationRef = useRef(0);

  // Archived segments background loading state
  const [archivedSegmentsLoaded, setArchivedSegmentsLoaded] = useState(false);
  const [archivedSegmentsError, setArchivedSegmentsError] = useState<
    string | null
  >(null);
  const isLoadingArchivedRef = useRef(false);

  // Wrap setSegments to update ref synchronously
  const setSegments = useCallback(
    (updater: SetStateAction<Record<string, Segment> | undefined>) => {
      setSegmentsState(prev => {
        const newData = typeof updater === "function" ? updater(prev) : updater;
        // Update ref synchronously so subsequent operations have latest data
        segmentsRef.current = newData;
        return newData;
      });
    },
    []
  );

  // DMP state
  const [dmps, setDmps] = useState<DataManagementPlatform[] | undefined>(
    undefined
  );
  const [dmpsLoading, setDmpsLoading] = useState(true);

  // Track which strategies have been requested to avoid duplicate fetches
  const requestedStrategiesRef = useRef<Set<RecipeLoadingStrategy>>(new Set());

  // Track pending field updates to preserve during merges
  // Map of recipeId -> partial updates that should override server data
  const pendingUpdatesRef = useRef<Map<string, Partial<Recipe>>>(new Map());

  // Track in-progress loads for diagnostics
  const inProgressLoadsRef = useRef<Set<Promise<void>>>(new Set());

  // ============================================================================
  // DATA LOADING FUNCTIONS
  // ============================================================================

  const loadRecipes = useCallback(
    async (strategy: RecipeLoadingStrategy) => {
      // Always read current strategy from ref to avoid stale closures
      const currentStrategy = recipesStrategyRef.current;

      // Skip if already loading this strategy (check first to prevent race conditions)
      if (requestedStrategiesRef.current.has(strategy)) {
        return;
      }

      // Skip if already loaded with compatible strategy
      if (canCacheSatisfyStrategy(currentStrategy, strategy)) {
        return;
      }

      // Mark as loading before any async work to prevent duplicate calls
      requestedStrategiesRef.current.add(strategy);

      // Set loading atomically to prevent context changes from affecting consumers mid-load
      setRecipeState(prev => ({ ...prev, loading: true }));

      const filters =
        strategy === RECIPE_LOADING_STRATEGY.MINIMAL
          ? MINIMAL_RECIPE_FILTERS
          : DEFAULT_RECIPE_FILTERS;

      // Create a promise for this load and track it
      const loadPromise = (async () => {
        try {
          const loadedRecipes = await firstValueFrom(
            madSDK.recipes.find(filters)
          );
          const recipesById = convertArrayToKeyedObject(loadedRecipes || []);

          // Merge with existing cache, applying pending updates
          // IMPORTANT: Update data AND strategy in single state update for atomicity
          setRecipeState(prev => {
            const pendingUpdates = pendingUpdatesRef.current;
            const prevRecipes = prev.data;

            // Start with previous recipes
            const merged = { ...prevRecipes };

            // Merge new recipes, applying pending updates
            Object.entries(recipesById).forEach(([id, recipe]) => {
              const pendingUpdate = pendingUpdates.get(id);

              if (!pendingUpdate) {
                // No pending update - merge normally
                merged[id] = recipe;
              } else {
                // Has pending update - merge server data but override with pending fields
                merged[id] = {
                  ...recipe, // Server data (including FULL expression)
                  ...pendingUpdate // Pending field overrides
                };
              }
            });

            // Update refs
            recipesStrategyRef.current = strategy;
            recipesRef.current = merged;

            // Return updated state with data, strategy, AND loading atomically
            return {
              data: merged,
              strategy: strategy,
              loading: false
            };
          });
        } catch (error) {
          console.error("Error loading recipes:", error);
          // Set loading to false on error
          setRecipeState(prev => ({ ...prev, loading: false }));
        } finally {
          requestedStrategiesRef.current.delete(strategy);
        }
      })();

      // Track this load
      inProgressLoadsRef.current.add(loadPromise);
      await loadPromise;
      inProgressLoadsRef.current.delete(loadPromise);
    },
    [] // No dependencies - all state read via refs to avoid stale closures
  );

  const loadSegments: () => Promise<void> = useCallback(async () => {
    // Use ref to read current segments value — avoids including `segments` in
    // deps which would change the callback identity on every re-render while
    // retries are in-flight, restarting the retry loop via useAudienceData's useEffect
    if (segmentsRef.current !== undefined) {
      return;
    }

    // Don't retry after all attempts are exhausted — unrelated re-renders
    // (e.g. dmps loading) would otherwise trigger a fresh cycle and clear the error
    if (segmentLoadFailedRef.current) {
      return;
    }

    // Guard against concurrent calls during backoff delays
    if (isLoadingSegmentsRef.current) {
      return;
    }
    isLoadingSegmentsRef.current = true;

    // A cycle superseded by an invalidation must not write its result or its
    // failure — it hands off to a fresh load instead.
    const generation = segmentsGenerationRef.current;
    const restartIfSuperseded = () => {
      if (generation === segmentsGenerationRef.current) {
        return null;
      }
      isLoadingSegmentsRef.current = false;
      setSegmentsLoading(false);
      return loadSegments();
    };

    setSegmentsLoading(true);
    setSegmentsError(null);

    const MAX_RETRIES = 3;
    const BASE_DELAY_MS = 1000;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const loadedSegments = await madSDK.dmps.segments.find(
          DEFAULT_SEGMENT_FILTERS
        );
        const restarted = restartIfSuperseded();
        if (restarted) {
          return restarted;
        }
        setSegments(convertArrayToKeyedObject(loadedSegments || []));
        setSegmentsLoading(false);
        isLoadingSegmentsRef.current = false;
        return;
      } catch (error) {
        const isLastAttempt = attempt === MAX_RETRIES - 1;
        if (isLastAttempt) {
          const restarted = restartIfSuperseded();
          if (restarted) {
            return restarted;
          }
          segmentLoadFailedRef.current = true;
          setSegmentsError("Failed to load segments. Please refresh the page.");
          setSegmentsLoading(false);
          isLoadingSegmentsRef.current = false;
          return;
        }
        // Exponential backoff before next attempt: 1s (attempt 0), 2s (attempt 1)
        await new Promise(resolve =>
          setTimeout(resolve, BASE_DELAY_MS * Math.pow(2, attempt))
        );
      }
    }
  }, []); // Empty deps — reads segments via ref, concurrent guard via isLoadingSegmentsRef

  const loadArchivedSegments = useCallback(async () => {
    if (isLoadingArchivedRef.current) {
      return;
    }
    isLoadingArchivedRef.current = true;

    try {
      const archivedSegments = await madSDK.dmps.segments.find(
        ARCHIVED_SEGMENT_FILTERS
      );
      const archivedById = convertArrayToKeyedObject(archivedSegments || []);

      // Merge in archived-only; never overwrite a segment already tracked
      // locally (default fetch or a live mutation), since this background
      // fetch can be slow and its snapshot may be stale by the time it resolves.
      setSegments(prev => ({ ...archivedById, ...prev }));
      setArchivedSegmentsLoaded(true);
    } catch {
      setArchivedSegmentsError(
        "Failed to load archived segments. Please refresh the page."
      );
      setArchivedSegmentsLoaded(true);
    } finally {
      isLoadingArchivedRef.current = false;
    }
  }, []); // Empty deps — concurrent guard via isLoadingArchivedRef

  const loadDmps = useCallback(async () => {
    if (dmps !== undefined) {
      return;
    }

    setDmpsLoading(true);

    try {
      const loadedDmps = await madSDK.dmps.find({});
      setDmps(loadedDmps || []);
    } catch (error) {
      // No-op: leave as undefined to allow retry
    } finally {
      setDmpsLoading(false);
    }
  }, [dmps]);

  // Segments published elsewhere in the app (e.g. a Maverick AI persona) are
  // missing from this cache until it drops what it holds and refetches. The ref
  // is cleared alongside the state because loadSegments reads it as its guard.
  useEffect(
    () =>
      subscribeToSegmentInvalidation(() => {
        segmentsGenerationRef.current += 1;
        segmentsRef.current = undefined;
        segmentLoadFailedRef.current = false;
        setSegments(undefined);
        setArchivedSegmentsLoaded(false);
        loadSegments();
      }),
    [setSegments, loadSegments]
  );

  // ============================================================================
  // MEMOIZED CONTEXT VALUE
  // ============================================================================

  // Memoize context value - only recreate when actual data changes
  // Intentionally exclude loading states, setState, and load functions to prevent
  // unnecessary re-renders. Consumers can read loading states from the current value.
  const contextValue = useMemo((): AudienceDataContextValue => {
    return {
      recipes,
      setRecipes,
      recipesStrategy,
      recipesLoading,
      setRecipesLoading,
      segments,
      setSegments,
      segmentsLoading,
      setSegmentsLoading,
      segmentsError,
      archivedSegmentsLoaded,
      archivedSegmentsError,
      dmps,
      setDmps,
      dmpsLoading,
      setDmpsLoading,
      loadRecipes,
      loadSegments,
      loadArchivedSegments,
      loadDmps,
      pendingUpdatesRef,
      inProgressLoadsRef,
      recipesRef,
      segmentsRef
    };
  }, [
    recipeState, // Single dependency - recipes and strategy update atomically
    segments,
    segmentsError, // Included so consumers re-render when error state changes
    archivedSegmentsLoaded,
    archivedSegmentsError,
    dmps
    // Note: Intentionally omitting transient loading states, setState, and load
    // functions to prevent cascading re-renders when loading starts/stops
  ]);

  return (
    <AudienceDataContext.Provider value={contextValue}>
      {children}
    </AudienceDataContext.Provider>
  );
};
